<template>
  <section class="flex flex-col md:flex-row w-full px-4 md:px-0">
    <!-- SIDEBAR -->
    <div class="hidden md:block w-96 flex-shrink-0">
      <div class="sidecard-base h-auto bg-white rounded-lg shadow-sm">
        <div class="p-2 sm:p-2 space-y-4 text-sm">
          <h3 class="text-lg sm:text-xl font-semibold text-gray-900 border-b pb-2 mb-3">
            Filter Archives
          </h3>

          <!-- YEAR LOOP DESCENDING WITH ACCORDION -->
          <div
            v-for="yearData in [...groupedArchiveCount].sort((a, b) => b.year - a.year)"
            :key="yearData.year"
            class="mb-4 border-b border-gray-200"
          >
            <!-- Year heading clickable -->
            <button
              @click="toggleYear(yearData.year)"
              class="w-full flex justify-between items-center py-2 font-bold text-gray-800 hover:text-blue-600 focus:outline-none"
            >
              <span
                >{{ yearData.year }} ({{
                  yearData.months.reduce((sum, m) => sum + m.count, 0)
                }}
                files)</span
              >
              <svg
                :class="{ 'rotate-180': isYearOpen(yearData.year) }"
                class="w-5 h-5 transition-transform duration-300"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 9l-7 7-7-7"
                ></path>
              </svg>
            </button>

            <!-- MONTHS LOOP (Accordion content) -->
            <transition name="accordion">
              <div v-show="isYearOpen(yearData.year)" class="ml-2 mt-2 space-y-1">
                <div
                  v-for="month in [...yearData.months].sort((a, b) => b.month - a.month)"
                  :key="month.month"
                  class="flex justify-between items-center p-2 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors duration-200"
                >
                  <span class="text-gray-700 font-medium text-sm">
                    {{ monthNames[month.month] }}
                  </span>
                  <span
                    class="text-xs sm:text-xs font-semibold text-zinc-700 px-2 py-0.5 rounded-full"
                  >
                    {{ month.count }}
                  </span>
                </div>
              </div>
            </transition>
          </div>
        </div>
      </div>
    </div>
    <!-- MAIN CONTENT -->
    <div class="flex-1 md:pl-6">
      <div class="relative bg-gray-100">
        <div class="absolute bottom-4 right-0">
          <div class="flex gap-2">
            <SmallWidgetSvg
              class="cursor-pointer"
              :class="activeLayout === 'small' ? 'text-black' : 'text-zinc-400'"
              @click="setLayout('small')"
            />
            <LargeWidgetSvg
              class="cursor-pointer"
              :class="activeLayout === 'large' ? 'text-black' : 'text-zinc-400'"
              @click="setLayout('large')"
            />
          </div>
        </div>
      </div>
      <div v-if="activeLayout === 'small'">
        <table class="w-full border border-gray-200 rounded-lg overflow-hidden text-sm">
          <thead class="bg-gray-100 text-gray-700 uppercase text-xs tracking-wider">
            <tr>
              <th class="p-3 text-left">File Name</th>
              <th class="p-3 text-left">Uploaded</th>
              <th class="p-3 text-center">Action</th>
            </tr>
          </thead>

          <tbody>
            <tr
              v-for="(item, index) in itemDocuments"
              :key="index"
              class="border-t hover:bg-gray-50 transition"
            >
              <!-- File Name -->
              <td class="p-3 flex items-center gap-2">
                <span>
                  <img
                    class="h-9 w-9"
                    :src="require(`@/assets/images/logo/${getLogo(item.file_type)}`)"
                    alt=""
                  />
                </span>
                <span class="truncate max-w-xs flex flex-col gap-1">
                  <span class="text-sm font-normal"> {{ item.original_filename }}</span>
                  <span class="text-xs text-zinc-500"> {{ item.description }}</span>
                </span>
              </td>

              <!-- Date -->
              <td class="p-3">
                {{ renderDateTimeFormat(item.createdAt) }}
              </td>

              <!-- Download -->
              <td class="p-3 flex justify-center">
                <button
                  @click="downloadFile(item.encrypted_filename)"
                  class="bg-zinc-700 hover:bg-zinc-600 text-white px-4 py-1 rounded-md text-xs transition flex gap-2 items-center"
                >
                  <DownloadSvg class="w-5 h-5" />
                  Download
                </button>
              </td>
            </tr>

            <!-- Empty State -->
            <tr v-if="!itemDocuments || itemDocuments.length === 0">
              <td colspan="3" class="text-center py-6 text-gray-500">
                No gazette files available.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else>
        <!-- Card layout in a row -->
        <div class="flex flex-wrap gap-4">
          <div
            v-for="(item, index) in itemDocuments"
            :key="index"
            class="flex flex-col gap-4 bg-white shadow-sm rounded-lg p-4 w-full transition hover:shadow-md"
          >
            <!-- File Info -->
            <div class="flex-1 flex flex-col gap-1">
              <span class="font-medium text-xl truncate tracking-wider">
                MEMORANDUM AGREEMENT NO. {{ index + 1 + 10000 }}
              </span>
              <span class="text-xs text-gray-400">
                {{ renderDateTimeFormat(item.createdAt) }}
              </span>
              <span class="text-sm text-zinc-600 truncate py-2">
                {{ item.description }}
              </span>

              <span
                class="inline-flex gap-2 max-w-72 items-center cursor-pointer bg-gray-100 hover:bg-gray-200 px-3 py-2 rounded-md transition"
                @click="downloadFile(item.encrypted_filename)"
              >
                <img
                  class="h-7 w-7"
                  :src="require(`@/assets/images/logo/${getLogo(item.file_type)}`)"
                  alt=""
                />
                <span class="text-sm font-normal truncate max-w-xs">
                  {{ item.original_filename }}
                </span>
              </span>
            </div>

            <!-- Download Button -->
            <button
              v-if="false"
              @click="downloadFile(item.encrypted_filename)"
              class="bg-zinc-700 w-64 hover:bg-zinc-600 text-white px-3 py-1.5 rounded-md text-xs transition"
            >
              Download
            </button>
          </div>

          <!-- Enhanced Empty State -->
          <div
            v-if="!itemDocuments || itemDocuments.length === 0"
            class="w-full flex flex-col items-center justify-center py-12 text-gray-400 space-y-2 border-2 border-dashed border-gray-200 rounded-lg"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-12 w-12 text-gray-300"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 17v-4h6v4m2 4H7a2 2 0 01-2-2V7a2 2 0 012-2h6l4 4v10a2 2 0 01-2 2z"
              />
            </svg>
            <p class="text-lg font-medium">No gazette files available</p>
            <p class="text-sm text-gray-500">Once uploaded, your files will appear here</p>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
<script>
import LargeWidgetSvg from '@/components/svgs/LargeWidgetSvg.vue';
import SmallWidgetSvg from '@/components/svgs/SmallWidgetSvg.vue';
import DownloadSvg from '@/components/svgs/DownloadSvg.vue';
import { inject, onMounted, ref, computed } from 'vue';
import reusableBase from '@/utils/reusables/dateFormat';
import useDecryptParam from '@/utils/decrypt';

export default {
  components: { LargeWidgetSvg, SmallWidgetSvg, DownloadSvg },
  setup() {
    const { decryptedValue: id } = useDecryptParam('id');
    const axios = inject('$axios');
    const itemDocuments = ref([]);
    const { renderDateTimeFormat } = reusableBase();
    const openYears = ref([]);

    // Use localStorage to persist layout
    const storedLayout = localStorage.getItem('activeLayout');
    const activeLayout = ref(storedLayout || 'small'); // default to 'small'

    const monthNames = [
      'JANUARY',
      'FEBRUARY',
      'MARCH',
      'APRIL',
      'MAY',
      'JUNE',
      'JULY',
      'AUGUST',
      'SEPTEMBER',
      'OCTOBER',
      'NOVEMBER',
      'DECEMBER'
    ];

    onMounted(() => {
      fetchDocuments();
    });

    const fetchDocuments = async () => {
      try {
        console.log(id.value);
        const result = await axios.get(`/api/document-groups/section/${id.value}`);
        console.log(result.data.data);
        itemDocuments.value = result.data.data[0].documents;
      } catch (error) {
        console.log('render', error);
      }
    };

    const getLogo = (file) => {
      const extension = file?.split('/')?.pop()?.toLowerCase();
      const logoMap = {
        pdf: 'pdf-logo.png',
        csv: 'excel-logo.png',
        xls: 'excel-logo.png',
        xlsx: 'excel-logo.png',
        doc: 'word-logo.png',
        docx: 'word-logo.png',
        jpeg: 'image-logo.png',
        jpg: 'image-logo.png',
        png: 'image-logo.png'
      };
      return logoMap[extension] || 'folder-logo.png';
    };

    const downloadFile = (file) => {
      const baseURL = 'https://archivegazette.iwcdeped.com/';
      window.open(baseURL + file, '_blank');
    };

    const groupedArchiveCount = computed(() => {
      if (!itemDocuments.value || itemDocuments.value.length === 0) return [];

      const grouped = {};

      itemDocuments.value.forEach((item) => {
        const date = new Date(item.createdAt);
        const year = date.getFullYear();
        const month = date.getMonth();

        if (!grouped[year]) grouped[year] = {};
        if (!grouped[year][month]) grouped[year][month] = 0;

        grouped[year][month] += 1;
      });

      const sortedYears = Object.keys(grouped)
        .map(Number)
        .sort((a, b) => b - a) // descending
        .map((year) => {
          const months = Object.keys(grouped[year])
            .map(Number)
            .sort((a, b) => a - b)
            .map((month) => ({ month, count: grouped[year][month] }));
          return { year, months };
        });

      return sortedYears;
    });

    const toggleYear = (year) => {
      if (openYears.value.includes(year)) {
        openYears.value = openYears.value.filter((y) => y !== year);
      } else {
        openYears.value.push(year);
      }
    };

    const isYearOpen = (year) => openYears.value.includes(year);

    // Persist layout to localStorage when changed
    const setLayout = (layout) => {
      activeLayout.value = layout;
      localStorage.setItem('activeLayout', layout);
    };

    return {
      fetchDocuments,
      itemDocuments,
      renderDateTimeFormat,
      getLogo,
      downloadFile,
      groupedArchiveCount,
      monthNames,
      openYears,
      isYearOpen,
      toggleYear,
      activeLayout,
      setLayout,
      id
    };
  }
};
</script>
<style>
.accordion-enter-from,
.accordion-leave-to {
  max-height: 0;
  opacity: 0;
  overflow: hidden;
}
.accordion-enter-to,
.accordion-leave-from {
  max-height: 500px; /* enough for months */
  opacity: 1;
}
.accordion-enter-active,
.accordion-leave-active {
  transition: all 0.3s ease;
}
</style>
